import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import type { AttendanceRecord } from '@tailoring-crm/shared';
import { AttendanceRow, type AttendanceRowProps } from './attendance-row.js';

/**
 * The day's roll call as a table.
 *
 * A table rather than the reflowing card rows the customer book uses, because this is seven
 * short columns read DOWN — "who has not come in yet" is a question answered by scanning one
 * column, not by reading forty cards. It scrolls sideways on a phone rather than collapsing into
 * a second layout nobody would keep in step with this one.
 */
/** The seven columns, in the order the day is read across. */
const COLUMNS = ['employee', 'role', 'status', 'checkIn', 'checkOut', 'hours', 'actions'] as const;

export interface AttendanceTableProps extends Omit<AttendanceRowProps, 'record' | 'pending'> {
  records: readonly AttendanceRecord[];
  /** The person a write is in flight for, if any. */
  pendingStaffUserId?: string | null;
}

export function AttendanceTable({
  records,
  pendingStaffUserId = null,
  ...rowProps
}: AttendanceTableProps): ReactElement {
  const { t } = useTranslation();

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs text-muted-foreground">
            {COLUMNS.map((column) => (
              <th key={column} scope="col" className="py-1 pr-3 font-medium">
                {t(`attendance.column.${column}`)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {records.map((record) => (
            <AttendanceRow
              key={record.id}
              record={record}
              pending={pendingStaffUserId === record.staffUserId}
              {...rowProps}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default AttendanceTable;
